/**
 * KanzleiUebergabeMonthEndBanner — WL-F1 Premium-Trigger 4, "Monatsende"-Surface
 * (modules-loop-b.md WL-F1). A GENUINE inline, non-blocking, dismissible banner
 * — no `fixed inset-0` backdrop, no modal overlay, no focus trap. It never
 * opens `PaywallModal` automatically on a passive page load; the full-screen
 * `PaywallModal` only appears if the user EXPLICITLY clicks the banner's
 * upgrade CTA (Reviewer Finding 2, dark-pattern gate — a passive month-end
 * reminder that auto-pops a full-screen modal on every eligible page load is
 * MORE intrusive than the spec's "Monatsende-**Banner**" wording and was
 * fixed here). See `PaywallModal` for the interruptive export-moment variant,
 * which IS user-action-triggered (fires right after a completed DATEV export
 * — a real, just-happened user action, not a passive page load).
 *
 * Same "Alles DATEV-fertig an Ihre Kanzlei übergeben"-motif as the modal, own
 * 7-day cooldown (`paywallTrigger4Suppression`, variant `'month_end'`) —
 * marked on the BANNER's own mount (the moment it is actually shown to the
 * user), so it never nags on every page load during the trailing window.
 * `paywall_shown` growth-event attribution stays scoped to `PaywallModal`
 * itself (the literal "paywall" surface with price-clarity CTA) — it fires
 * when the user clicks through from this banner, not for the passive banner
 * impression, keeping `paywall_shown` semantics ("the paywall was shown")
 * strictly correct and avoiding a second Firebase-callable side-effect on
 * every eligible page load.
 *
 * Outer/Inner split (pattern: `GlobalUnreadBadge` in MainLayout.tsx) — the
 * outer component is the visibility GATE (beta + not-premium + own-scope +
 * month-end window + cooldown); the inner component is only mounted once all
 * gates pass, so the `useAuth()`-derived checks never even run their effects
 * for the vast majority of renders (normal users, non-month-end days, already
 * premium, …).
 *
 * Beta-gating: rein kosmetisch (`isLoopBBeta`), wie alle Loop-B-FE-Gates — der
 * Server erzwingt nichts hier (dies ist reine Werbe-UI, kein Zugriffsschutz).
 *
 * @module components/premium/KanzleiUebergabeMonthEndBanner
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconClose, IconAdvisor } from '../../icons';

import { useAuth } from '../../context/AuthContext';
import { isLoopBBeta } from '../../config/loopBBetaAccess';
import { isWithinMonthEndWindow } from '../../utils/monthEndWindow';
import {
  markKanzleiUebergabePaywallShown,
  shouldShowKanzleiUebergabePaywall,
} from '../../utils/paywallTrigger4Suppression';
import PaywallModal from './PaywallModal';

/**
 * Inner component — only mounted when the outer gate passes. Renders an
 * INLINE banner (own local `dismissed` state — an X-dismiss hides the banner
 * for the rest of this mount; the 7-day cooldown, marked on mount below, is
 * what actually prevents it from reappearing on the next page load). Opens
 * `PaywallModal` (variant `'month_end'`) ONLY on an explicit CTA click —
 * never automatically.
 */
const KanzleiUebergabeMonthEndBannerInner: React.FC = () => {
  const { t } = useTranslation(['subscription', 'common']);
  const [dismissed, setDismissed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    // Marks the SAME cooldown the outer gate reads (`shouldShowKanzleiUebergabePaywall`)
    // at the moment the banner itself becomes visible — this is what keeps the
    // banner from reappearing on every page load within the month-end window.
    markKanzleiUebergabePaywallShown('month_end');
    // Fire exactly once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (dismissed) return null;

  return (
    <>
      <div
        role="status"
        className="mb-4 flex flex-col gap-3 rounded-lg border border-indigo-600/60 bg-[var(--tone-accent-bg)] p-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-indigo-600/20 text-accent-ink">
            <IconAdvisor size={16} />
          </span>
          <div>
            <p className="text-sm font-semibold text-[var(--tone-accent-fg)]">
              {t('paywallKanzlei.monthEnd.title', 'Alles DATEV-fertig an Ihre Kanzlei übergeben')}
            </p>
            <p className="mt-0.5 text-xs text-[var(--tone-accent-fg)]">
              {t(
                'paywallKanzlei.monthEnd.message',
                'Das Monatsende naht. Mit Premium erstellen Sie unbegrenzt weitere Rechnungen und übergeben lückenlos an Ihre Kanzlei.',
              )}
            </p>
            <p className="mt-1 text-[11px] text-accent-ink">
              {t(
                'paywallKanzlei.priceNote',
                'Aktuelle Preise finden Sie transparent auf der nächsten Seite — keine automatische Buchung.',
              )}
            </p>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2 self-end sm:self-auto">
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-indigo-500"
          >
            {t('paywallKanzlei.cta', 'Premium ansehen')}
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label={t('close', { ns: 'common' })}
            className="text-accent-ink transition-colors hover:text-primary"
          >
            <IconClose size={14} />
          </button>
        </div>
      </div>

      {/* User-initiated ONLY — never mounted on the passive page load itself. */}
      {modalOpen && (
        <PaywallModal
          trigger="kanzlei_uebergabe"
          variant="month_end"
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};

/**
 * Outer visibility gate. Fail-closed: renders `null` unless EVERY condition
 * holds — Loop-B-beta, not already premium, viewing the user's OWN scope (not
 * an advisor browsing a mandant), within the trailing month-end window, and
 * the 7-day per-variant cooldown has elapsed.
 */
const KanzleiUebergabeMonthEndBanner: React.FC = () => {
  const { currentUser, isPremium, sellerData, effectiveScopeUid } = useAuth();

  // `useState(() => new Date())` would freeze "today" at first mount — that's
  // fine here (a banner mounted once per page-load doesn't need live ticking).
  const [today] = useState(() => new Date());

  const isOwnScope = !!currentUser && effectiveScopeUid === currentUser.uid;
  const eligible =
    isLoopBBeta(currentUser?.uid, sellerData) &&
    !isPremium &&
    isOwnScope &&
    isWithinMonthEndWindow(today) &&
    shouldShowKanzleiUebergabePaywall('month_end');

  // Only mounts the Inner component once every gate passes. `eligible` is
  // recomputed on every render from live `useAuth()` state — no polling
  // needed, a fresh page-load/remount already re-checks (e.g. after upgrading
  // to premium, the next navigation stops rendering this banner).
  if (!eligible) return null;
  return <KanzleiUebergabeMonthEndBannerInner />;
};

export default KanzleiUebergabeMonthEndBanner;
